/**
 * App Download Banner Configuration
 * 
 * This file contains the configuration for which pages should show the app download banner
 * and what parameters to use for each page.
 */

import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

// Re-export types for convenience
export interface AppDownloadBannerConfig {
    /** Navigation parameter for app routing (e.g., 'home', 'search', 'messages') */
    nav?: string;
    /** Campaign name for Adjust tracking */
    campaign: string;
    /** Ad group name for Adjust tracking */
    adgroup: string;
    /** Creative name for Adjust tracking */
    creative: string;
    /** Storage key suffix for persistence */
    storageKey: string;
    /** Adjust link - can be a static string or a function that generates the link */
    adjustLink: string | ((params?: Record<string, string>, deviceOS?: string) => string);
    /** 
     * Optional experiment control for this page
     * If specified, the banner will only show when the experiment variant matches
     */
    experiment?: {
        /** GrowthBook feature key */
        featureKey: string;
        /** Expected variant value(s) to show the banner */
        enabledVariants: string[];
    };
}

/**
 * Page type identifier - using string to avoid dependency on shell package
 */
type PageType = string;

/**
 * Configuration for App Download Banner per page type
 * Only pages listed here will show the banner
 * 
 * Page types should match the route names from shell/route-map
 */
export const APP_DOWNLOAD_BANNER_CONFIG: Partial<Record<PageType, AppDownloadBannerConfig>> = {
    'home': {
        nav: 'home',
        campaign: 'Mobile_Web_Banner',
        adgroup: 'Home_Page',
        creative: 'V1',
        storageKey: 'home',
        adjustLink: 'https://gumtree.go.link/kIegC',
    },
    'vip': {
        nav: '', // or dynamic path like 'p/{adId}'
        campaign: 'Mobile_Web_Banner',
        adgroup: 'VIP_Page',
        creative: 'V1',
        storageKey: 'vip',
        adjustLink: (params, deviceOS) => {
            // iOS uses different URL format than Android
            const baseUrl = deviceOS === 'IOS'
                ? 'https://gumtree.go.link?nav=viewAd&adj_t=1zk5onwq&adj_campaign=Mobile_Web_Banner&adj_adgroup=VIP_Page&adj_creative=V1'
                : 'https://gumtree.go.link/link?nav=viewAd&adj_t=1zk5onwq&adj_campaign=Mobile_Web_Banner&adj_adgroup=VIP_Page&adj_creative=V1';
            
            if (params?.adID) {
                // return `${baseUrl}&adID=${deviceOS === 'IOS' ? params.adID : '1512091606'}`; // for test android
                return `${baseUrl}&adID=${params.adID}`;
            }
            return baseUrl;
        },
        experiment: {
            featureKey: GrowthBookFeature.INVOKE_1,
            enabledVariants: ['B'], // Only show when variant is B
        },
    },
    // Add more page configurations here as needed
};

/**
 * Get banner configuration for a specific page type
 * @param pageType - Current page type (e.g., 'home', 'vip', 'srp')
 * @returns Banner configuration if the page should show banner, undefined otherwise
 */
export function getBannerConfig(pageType: PageType): AppDownloadBannerConfig | undefined {
    return APP_DOWNLOAD_BANNER_CONFIG[pageType];
}

/**
 * Map GA4 PageType to route page type
 * @param ga4PageType - GA4 page type (e.g., 'Homepage', 'VIP', 'ResultsSearch')
 * @returns Route page type identifier (e.g., 'home', 'vip', 'srp')
 */
export function mapGa4PageTypeToRouteType(ga4PageType: string | undefined): PageType | undefined {
    if (!ga4PageType) {
        return undefined;
    }

    // Map GA4 PageType to route page type
    switch (ga4PageType) {
        case 'Homepage':
            return 'home';
        case 'VIP':
        case 'pVIP':
        case 'eVIP':
            return 'vip';
        case 'ResultsSearch':
        case 'ResultsBrowse':
            return 'srp';
        // Add more mappings as needed
        default:
            return undefined;
    }
}

